<script lang="ts" setup>
  import Popover from '@/components/ui/popover/Popover.vue'
  import PopoverContent from '@/components/ui/popover/PopoverContent.vue'
  import PopoverTrigger from '@/components/ui/popover/PopoverTrigger.vue'
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useTranslation } from '@/composables/useTranslation'
  import { entitiesDE } from '@/i18n/entity'
  import type { CustomerCase } from '@/types/generated'
  import { EntityTypeEnum } from '@/types/generated'
  import { BuildingIcon, InfoIcon, UserIcon, UsersIcon, XIcon } from 'lucide-vue-next'
  import { ref } from 'vue'

  const { t } = useTranslation({
    en: {
      Participant: 'Party',
      Participants: 'Parties',
      KIChatHasAccessToAllDataInTheRequest: 'KI-Chat has access to all data in the request.',
      UnknownRole: 'Unknown Role'
    },
    de: {
      Participant: 'Beteiligter',
      Participants: 'Beteiligte',
      KIChatHasAccessToAllDataInTheRequest: 'KI-Chat hat Zugriff auf alle Daten in der Anfrage.',
      UnknownRole: 'Unbekannte Rolle'
    }
  })

  const props = defineProps<{
    caseData: CustomerCase | null
  }>()

  const isOpen = ref(false)
  const showInfoBanner = ref(true)
  const ROLE_MAPPING = entitiesDE

  const formatEntityName = (entity: any) => {
    if (entity.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      return `${entity.first_name} ${entity.last_name}`
    }
    if (entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      return `${entity.company_name} ${entity.company_legal_form}`
    }
    return entity.company_name
  }

  const { showCard } = useEntityCard()

  const handleEntityClick = (entity: any, element: HTMLElement) => {
    const entityWithName = {
      ...entity,
      name: formatEntityName(entity)
    }
    showCard(entityWithName, element)
  }
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger
      class="flex items-center gap-1.5 text-xs hover:text-gray-700 dark:hover:text-neutral-300"
    >
      <UsersIcon class="h-3.5 w-3.5" />
      <span>
        {{
          props.caseData?.data?.entities?.length === 1
            ? `1 ${t('Participant')}`
            : `${props.caseData?.data?.entities?.length || 0} ${t('Participants')}`
        }}
      </span>
    </PopoverTrigger>

    <PopoverContent
      class="w-[320px] p-0"
      side="bottom"
      align="end"
      :side-offset="8"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 class="text-base font-medium">
          {{ t('Participants') }} ({{ props.caseData?.data?.entities?.length || 0 }})
        </h3>
        <button
          class="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-300"
          @click="isOpen = false"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div>

      <!-- Info Banner -->
      <div
        v-if="showInfoBanner"
        class="mx-4 mt-2 flex items-center gap-2 rounded-md border border-blue-800 bg-blue-50 p-2 text-sm
          text-blue-700 dark:bg-blue-950 dark:text-blue-300"
      >
        <InfoIcon class="h-4 w-4 shrink-0" />
        <span>{{ t('KIChatHasAccessToAllDataInTheRequest') }}</span>
        <button
          class="ml-auto"
          @click="showInfoBanner = false"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div>

      <!-- Participants List -->
      <div class="max-h-[300px] overflow-y-auto px-4 py-2">
        <template
          v-for="entity in props.caseData?.data?.entities"
          :key="entity.id"
        >
          <div class="mb-3 last:mb-0">
            <div
              class="group -mx-2 flex cursor-pointer items-center gap-3 rounded-sm px-2 py-2 hover:bg-muted/50"
              @click="handleEntityClick(entity.entity, $event.currentTarget)"
            >
              <div
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-neutral-800"
              >
                <component
                  :is="
                    entity.entity.entity_type === EntityTypeEnum.NATURAL_PERSON
                      ? UserIcon
                      : BuildingIcon
                  "
                  class="h-4 w-4 text-gray-500 dark:text-neutral-400"
                />
              </div>
              <div class="flex min-w-0 flex-col">
                <div class="truncate text-sm text-muted-foreground">
                  {{ ROLE_MAPPING[entity.relationships[0]?.relationship_type] || t('UnknownRole') }}
                </div>
                <span class="truncate text-sm">
                  {{ formatEntityName(entity.entity) || '-' }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </PopoverContent>
  </Popover>
</template>
